<script setup lang="ts">
import { Loader2 } from 'lucide-vue-next'
import { useIntervalFn } from '@vueuse/core'

const props = withDefaults(
  defineProps<{
    mode?: 'convert' | 'analyze'
  }>(),
  {
    mode: 'analyze'
  }
)

const { t, tm } = useI18n()

const TIP_ROTATION_INTERVAL_S = 8

const tipIndex = ref(0)
const tipRotationTick = ref(0)

const title = computed(() =>
  props.mode === 'convert'
    ? t('pages.import.importPage.readingFile.convertingTitle')
    : t('pages.import.importPage.readingFile.title')
)

const tips = computed(() => {
  const key =
    props.mode === 'convert'
      ? 'pages.import.importPage.readingFile.convertingTips'
      : 'pages.import.importPage.readingFile.analyzeTips'
  const messages = tm(key)

  return Array.isArray(messages) ? (messages as string[]) : []
})

const currentTip = computed(() => {
  if (!tips.value.length) {
    return props.mode === 'convert'
      ? t('pages.import.importPage.readingFile.convertingSubtitle')
      : t('pages.import.importPage.readingFile.subtitle')
  }

  return tips.value[tipIndex.value % tips.value.length]
})

const durationHint = computed(() =>
  props.mode === 'convert'
    ? t('pages.import.importPage.readingFile.convertingDurationHint')
    : t('pages.import.importPage.readingFile.analyzeDurationHint')
)

useIntervalFn(() => {
  tipRotationTick.value += 1

  if (tips.value.length > 1 && tipRotationTick.value % TIP_ROTATION_INTERVAL_S === 0) {
    tipIndex.value += 1
  }
}, 1000)
</script>

<template>
  <Teleport to="body">
    <div
      class="fixed inset-0 z-[100] flex items-center justify-center bg-background/80 backdrop-blur-sm"
      @click.stop.prevent
      @mousedown.stop.prevent
    >
      <div
        class="flex w-full max-w-md flex-col items-center gap-4 rounded-xl border bg-card p-8 text-center shadow-lg"
        @click.stop
      >
        <Loader2 class="h-10 w-10 animate-spin text-primary" />
        <div class="space-y-3">
          <p class="text-base font-medium text-foreground">
            {{ title }}
          </p>
          <p class="min-h-10 text-sm text-muted-foreground transition-opacity duration-300">
            {{ currentTip }}
          </p>
          <p class="text-xs text-muted-foreground">
            {{ durationHint }}
          </p>
        </div>
      </div>
    </div>
  </Teleport>
</template>
